import { useRef, useState } from 'react';
import { Paperclip } from 'lucide-react';
import { Button } from './Button';
import { UPLOAD_ACCEPT, fileSize, uploadMedia, type MediaFile } from '@/lib/upload';

function message(e: unknown): string {
  const r = (e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }).response;
  const first = r?.data?.errors ? Object.values(r.data.errors)[0]?.[0] : undefined;
  return first ?? r?.data?.message ?? (e as Error)?.message ?? 'Upload failed.';
}

/**
 * Picks a file and uploads it to `media/upload`; calls `onUploaded` with the stored file.
 * Use for attachments, receipts and signatures instead of asking for a typed URL.
 */
export function FileUpload({ onUploaded, label = 'Attach file', accept = UPLOAD_ACCEPT, disabled, relatedType, validate }: { onUploaded: (f: MediaFile) => void; label?: string; accept?: string; disabled?: boolean; relatedType?: string; validate?: (file: File) => string | null }) {
  const input = useRef<HTMLInputElement>(null);
  const [pct, setPct] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pick = async (file?: File | null) => {
    if (!file) return;
    const invalid = validate?.(file);
    if (invalid) { setError(invalid); if (input.current) input.current.value = ''; return; }
    setError(null); setPct(0);
    try {
      const f = await uploadMedia(file, { relatedType, onProgress: setPct });
      onUploaded(f);
    } catch (e) {
      setError(message(e));
    } finally {
      setPct(null);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <div className="space-y-1">
      <input ref={input} type="file" accept={accept} className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
      <Button type="button" variant="ghost" disabled={disabled || pct !== null} onClick={() => input.current?.click()} className="inline-flex items-center gap-2">
        <Paperclip size={14} /> {pct !== null ? `Uploading… ${pct}%` : label}
      </Button>
      {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
    </div>
  );
}

/** A removable list of uploaded files. */
export function FileList({ files, onRemove }: { files: { name: string; url: string; size?: number }[]; onRemove?: (index: number) => void }) {
  if (!files.length) return null;
  return (
    <ul className="text-sm space-y-1">
      {files.map((f, i) => (
        <li key={`${f.url}-${i}`} className="flex items-center justify-between gap-2 border rounded-lg px-2 py-1">
          <a href={f.url} target="_blank" rel="noreferrer" className="text-brand-600 underline truncate">{f.name}</a>
          <span className="flex items-center gap-2 shrink-0">
            {f.size ? <span className="text-xs text-slate-400">{fileSize(f.size)}</span> : null}
            {onRemove && <button type="button" className="text-xs text-red-600" onClick={() => onRemove(i)}>Remove</button>}
          </span>
        </li>
      ))}
    </ul>
  );
}
